import dayjs from 'dayjs'
import { wrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldValue,
  FieldValues,
} from 'features/libra/ontology/ontologyFieldTypes'
import type { FieldOriginOption } from 'features/libra/ticket/fieldOrigin'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react'

import { NiiReportFields, useNiiLocalFieldValues } from './niiReportApi'

interface NiiReportDataContextValue {
  localValues: FieldValues
  setLocalFieldValue: (
    key: string,
    value: FieldValue,
    option?: FieldOriginOption
  ) => void
  applyAiLocalFieldValues: (incoming: FieldValues) => void
  hasUnsavedEditsOrSystemValues: boolean
  hasUnsavedEdits: boolean
  doSave: () => Promise<void>
  resetChanges: () => void
  isLoading: boolean
  isSaving: boolean
}

const NiiReportDataContext = createContext<NiiReportDataContextValue | null>(
  null
)

export const NiiReportDataProvider: FC<
  PropsWithChildren<{ fields: NiiReportFields }>
> = ({ children, fields }) => {
  const { fieldValues: ticketFieldValues } = useTicketAccess()

  const defaults = useMemo<FieldValues>(() => {
    const result: FieldValues = {}
    if (!ticketFieldValues[fields.analysisDate]) {
      result[fields.analysisDate] = wrapFieldValue(dayjs().format('YYYY-MM-DD'))
    }
    return result
  }, [ticketFieldValues, fields.analysisDate])

  const value = useNiiLocalFieldValues(fields, defaults)
  return (
    <NiiReportDataContext.Provider value={value}>
      {children}
    </NiiReportDataContext.Provider>
  )
}

export const useNiiReportData = (): NiiReportDataContextValue => {
  const ctx = useContext(NiiReportDataContext)
  if (!ctx)
    throw new Error(
      'useNiiReportData must be used within NiiReportDataProvider'
    )
  return ctx
}
